import * as React from 'react';
import { Button, FieldMessage, Modal, Select, Statuses } from '@teamsnap/teamsnap-ui';
import './transfer-participant-modal.scss';
import formatDivisionsAndTeams, { sortByLabel } from '../formatDivisionsAndTeams';
import Member from 'models/member';
import DivisionsAndTeamsQuery from 'core/gql/queries/divisions-and-teams';
import MUTATION from 'core/gql/mutations/transfer-member';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import SeasonParticipant from 'models/season-participant';
import * as QueryPrograms from '../../../core/gql/queries/programs';
import { updateSeasonParticipantCache } from '../season-participants-service';

type Props = {
  member: Member;
  show: boolean;
  setShow: (x: boolean) => void;
  orgId: number;
  contextSeasonId?: number;
};

const nonDeletableRoles = ['Commissioner', 'League Owner', 'Team Owner'];

const defOption = { label: '' };

const showFeedback = (success: boolean) => {
  reactiveFeedbackMessage({
    message: success ? 'Participant transferred to Team/Division' : 'Failed to transfer participant. Try again',
    autoclose: success,
    isError: !success,
  });
};

const canTransferParticipation = (participation: SeasonParticipant) =>
  !participation.participationRoles ||
  participation.participationRoles.every((x) => !nonDeletableRoles.includes(x.name));

const formatName = ({ firstName, lastName }) => {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }

  return firstName ? firstName : lastName;
};

const TransferModal = ({ show, setShow, member, orgId, contextSeasonId }: Props) => {
  const [from, setFrom] = React.useState<number>();
  const [toDivision, setToDivision] = React.useState<number>();
  const [attemptedSubmit, setAttemptedSubmit] = React.useState(false);
  const [selectedSeason, setSelectedSeason] = React.useState<number>();
  const [toTeam, setToTeam] = React.useState<string>();
  const [transferMember, { data: memberResponse, loading }] = useMutation(MUTATION);
  const [fetchSeason, { data: seasonData }] = useLazyQuery(DivisionsAndTeamsQuery);
  const { data: programsData } = useQuery(QueryPrograms.QUERY_WITH_SEASONS, {
    variables: { organizationId: orgId },
  });

  React.useEffect(() => {
    if (selectedSeason) {
      fetchSeason({ variables: { seasonId: selectedSeason } });
    }
  }, [selectedSeason, fetchSeason]);

  React.useEffect(() => {
    if (member.seasonParticipants.length) setFrom(member.seasonParticipants[0].seasonParticipantId);
  }, [member.seasonParticipants]);

  React.useEffect(() => {
    if (seasonData?.showSeason && seasonData.showSeason.divisions.length === 0) {
      setToDivision(parseInt(seasonData.showSeason.tsDivisionId));
    }
  }, [setToDivision, seasonData, selectedSeason]);

  const seasonsFilter = programsData?.programs
    ? [
        defOption,
        ...programsData.programs
          .flatMap(({ name, seasons }) => seasons.map((x) => ({ value: x.id, label: `${name} (${x.name})` })))
          .sort(sortByLabel),
      ]
    : [];

  const [divisions, teams] = formatDivisionsAndTeams(selectedSeason ? seasonData : null, toDivision);

  const possibleTransferParticipations = member.seasonParticipants.filter(canTransferParticipation);

  const participantTeams = possibleTransferParticipations
    .map(({ team, division, seasonParticipantId }) => {
      const divisionName = division?.name ?? 'No Division';
      const teamName = team?.name ?? 'No Team';
      return { value: seasonParticipantId, label: `${teamName} (${divisionName})` };
    })
    .sort((x, y) => x.label.toLowerCase().localeCompare(y.label.toLowerCase()));

  const close = () => {
    setShow(false);
    setAttemptedSubmit(false);
    setFrom(undefined);
    setSelectedSeason(0);
    setToDivision(undefined);
    setToTeam(undefined);
  };

  const onSubmit = async () => {
    setAttemptedSubmit(true);
    if (!selectedSeason || !toDivision) return;

    const seasonParticipantType = member.seasonParticipants.find(
      (x) => x.seasonParticipantId === from
    ).seasonParticipantType;

    try {
      await transferMember({
        variables: {
          seasonId: selectedSeason,
          contextSeasonId: contextSeasonId && `${contextSeasonId}`,
          organizationId: orgId,
          divisionId: toDivision,
          memberId: parseInt(member.id),
          teamId: toTeam === 'noTeam' ? undefined : parseInt(toTeam),
          seasonParticipantId: from,
          seasonParticipantType,
        },
        update(cache, { data: memberResponse }) {
          updateSeasonParticipantCache([memberResponse.transferSeasonParticipant]);
        },
      });

      showFeedback(true);
      close();
    } catch (e) {
      showFeedback(false);
    }
  };

  return (
    <Modal
      heading={`Transfer ${formatName(member)}'s profile`}
      style={{ minWidth: '0', width: '420px', textAlign: 'start' }}
      show={show}
      showClose
      allowOverlayClose
      closeFn={close}
    >
      <div className="modal-container">
        <div>
          {!possibleTransferParticipations.length && (
            <p className="u-textLeft">Commissioners, League Owners and Team Owners can not be transferred!</p>
          )}

          {!!possibleTransferParticipations.length && (
            <>
              <p className="u-textLeft">
                <b>Warning:</b> All statistics, availabilities, payments and other
                <br /> team-specific information will be deleted when a player is
                <br /> transferred to another team or unassigned. Proceed with caution.
              </p>

              <label htmlFor="from" className="FieldGroup-label u-spaceTopMd u-textLeft">
                Transfer From
              </label>
              <Select
                name="from"
                mods="u-spaceBottomMd"
                options={participantTeams}
                inputProps={{
                  value: from,
                  onChange: (e) => setFrom(parseInt(e.target.value)),
                }}
              />

              <label htmlFor="toDivision" className="FieldGroup-label u-spaceTopMd u-textLeft">
                Transfer to
              </label>
              <Select
                name="season"
                options={seasonsFilter}
                inputProps={{
                  value: selectedSeason,
                  onChange: (e) => setSelectedSeason(parseInt(e.target.value)),
                }}
              />
              {attemptedSubmit && !selectedSeason && (
                <FieldMessage status={Statuses.ERROR}>Select a program</FieldMessage>
              )}

              <Select
                name="toDivision"
                mods="u-spaceBottomMd u-spaceTopMd"
                options={divisions}
                inputProps={{
                  value: toDivision,
                  onChange: (e) => setToDivision(parseInt(e.target.value)),
                }}
              />

              <Select
                name="toTeam"
                mods="u-spaceBottomMd"
                options={teams}
                inputProps={{
                  value: toTeam,
                  onChange: (e) => setToTeam(e.target.value),
                }}
              />
            </>
          )}

          <div className="buttons-wrapper">
            <Button mods="u-spaceRightSm" onClick={close}>
              Cancel
            </Button>
            <Button
              mods="Button Button--primary"
              onClick={onSubmit}
              isDisabled={!possibleTransferParticipations.length || loading}
            >
              Transfer
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

TransferModal.defaultProps = {
  contextSeasonId: null,
};

export default TransferModal;
